// ── Motywy okazjonalne (kampanie operatora) ────────────────────────────────
// Trzeci rodzaj motywu tła Biurka — obok systemowych i własnych użytkownika.
// W przeciwieństwie do nich pochodzą od OPERATORA aplikacji i są sterowane
// harmonogramem (kadencja + okno aktywności + limit wyświetleń). Mają opcjonalną
// treść (karta z tytułem/komunikatem/CTA), są odrzucalne i limitowane.
//
// TEN PLIK TO ATRAPA. Docelowo lista należnych kampanii oraz licznik wyświetleń
// i odrzucenia pochodzą z backendu — patrz docs/backend-occasional-themes.md.
// Tu logikę harmonogramu i stan per-użytkownik symulujemy na localStorage, aby
// można było ocenić UI bez backendu.

export type OccasionalKind = "welcome" | "announcement";

export interface OccasionalContent {
  title: string;
  body: string;
  cta?: { label: string; url: string };
}

// ── Treść motywu powitalnego (kind: "welcome") ─────────────────────────────
// Komunikat powitalny + lista filmów instruktażowych pogrupowanych wg grup
// funkcji programu; wybrany film otwiera się w osadzonym odtwarzaczu Vimeo.
export interface WelcomeVideo {
  id: string;
  title: string;
  group: string; // grupa funkcji programu (nagłówek na liście)
  vimeoId: string; // identyfikator filmu w Vimeo
  duration?: string; // np. "4:30"
}
export interface WelcomeContent {
  heading: string;
  intro: string; // komunikat powitalny z odesłaniem do listy filmów
  videos: WelcomeVideo[];
}

export interface OccasionalSchedule {
  // accountActivation: kadencja liczona od dnia aktywacji konta (motyw powitalny
  // i jego cykliczne powtórzenia). absolute: sztywne okno dat (np. webinar).
  anchor: "accountActivation" | "absolute";
  from?: string; // YYYY-MM-DD (dla absolute)
  to?: string; // YYYY-MM-DD (dla absolute)
  // Dni po aktywacji, w których motyw ma się pojawić (dla accountActivation).
  cadenceDays?: number[];
  // Limit wyświetleń łącznie oraz czy użytkownik może trwale odrzucić.
  maxImpressions: number;
  dismissible: boolean;
}

export interface OccasionalTheme {
  id: string;
  kind: OccasionalKind;
  image: string; // tło-zdjęcie (puste, gdy używamy gradientu bg)
  bg?: string; // neutralne tło gradientowe (zamiast zdjęcia)
  content?: OccasionalContent; // karta komunikatu (announcement)
  welcome?: WelcomeContent; // bogaty layout powitalny (welcome)
  schedule: OccasionalSchedule;
  priority: number; // wyższy = ważniejszy przy kilku należnych jednocześnie
}

// Identyfikator demonstracyjnego, publicznie osadzalnego filmu Vimeo. Docelowo
// każdy film ma własny vimeoId dostarczony przez BE — tu jeden wspólny, aby
// odtwarzacz działał w podglądzie projektu.
const DEMO_VIMEO = "76979871";

const PICSUM = (id: number) => `https://picsum.photos/id/${id}/1600/1000`;

// ── Atrapa kampanii operatora ───────────────────────────────────────────────
// Webinar: okno względem „dziś", by było widoczne w demonstracji.
const isoShift = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const MOCK_CAMPAIGNS: OccasionalTheme[] = [
  {
    id: "welcome-2026",
    kind: "welcome",
    image: "", // bez zdjęcia — neutralne, czyste tło spójne z aplikacją
    bg: "linear-gradient(135deg, #f5f4f9 0%, #eceaf3 100%)",
    welcome: {
      heading: "Witamy w notaup.pl!",
      intro:
        "Cieszymy się, że jesteś z nami. Poniżej znajdziesz filmy wprowadzające w poszczególne grupy funkcji programu — wybierz temat z listy, a film otworzy się w odtwarzaczu obok. Zacznij od „Wprowadzenia”, a potem przejdź do obszarów, których będziesz używać najczęściej.",
      // Stała lista 6 filmów (2 kolumny × 3 wiersze).
      videos: [
        { id: "v0", group: "Pierwsze kroki", title: "Wprowadzenie do notaup.pl", vimeoId: DEMO_VIMEO, duration: "3:10" },
        { id: "v1", group: "Teczki i sprawy", title: "Zakładanie teczki i prowadzenie sprawy", vimeoId: DEMO_VIMEO, duration: "5:20" },
        { id: "v2", group: "Poczta i e-Doręczenia", title: "Obsługa poczty i e-Doręczeń", vimeoId: DEMO_VIMEO, duration: "6:00" },
        { id: "v3", group: "Budżetowanie", title: "Zaangażowania i lista wydatków", vimeoId: DEMO_VIMEO, duration: "7:15" },
        { id: "v4", group: "Akceptacja i Podpis", title: "Procedury akceptacji i podpis dokumentów", vimeoId: DEMO_VIMEO, duration: "5:50" },
        { id: "v5", group: "Biurko i terminarz", title: "Terminarz, zadania i kontakty", vimeoId: DEMO_VIMEO, duration: "4:25" },
      ],
    },
    schedule: {
      anchor: "accountActivation",
      cadenceDays: [0, 3, 7, 14],
      maxImpressions: 4,
      dismissible: false,
    },
    priority: 100,
  },
  {
    id: "webinar-budzet-2026-06",
    kind: "announcement",
    image: PICSUM(1067),
    content: {
      title: "Webinar: nowy moduł budżetowania",
      body: "Pokażemy zaangażowania, listę wydatków i raporty. Dołącz na żywo i zadaj pytania.",
      cta: { label: "Zapisz się na webinar", url: "https://notaup.pl/webinar/budzet" },
    },
    schedule: {
      anchor: "absolute",
      from: isoShift(-3),
      to: isoShift(7),
      maxImpressions: 2,
      dismissible: true,
    },
    priority: 90,
  },
];

// ── Stan per-użytkownik (ATRAPA na localStorage) ────────────────────────────
const ACTIVATION_LS = "desk_mock_account_activation";
const IMPRESSIONS_LS = "desk_occasional_impressions"; // { [id]: number }
const DISMISSED_LS = "desk_occasional_dismissed"; // string[]

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};
const writeJson = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* brak dostępu do localStorage — ignorujemy (atrapa) */
  }
};

// Data aktywacji konta (atrapa) — przy pierwszym uruchomieniu = dziś.
const getActivationDate = (): Date => {
  let iso = localStorage.getItem(ACTIVATION_LS);
  if (!iso) {
    iso = new Date().toISOString().slice(0, 10);
    try {
      localStorage.setItem(ACTIVATION_LS, iso);
    } catch {
      /* ignore */
    }
  }
  return new Date(iso);
};

const daysBetween = (a: Date, b: Date) =>
  Math.floor((b.getTime() - a.getTime()) / 86400000);

const getImpressions = (): Record<string, number> =>
  readJson<Record<string, number>>(IMPRESSIONS_LS, {});
const getDismissed = (): string[] => readJson<string[]>(DISMISSED_LS, []);

// Czy kampania jest należna „dziś" wg harmonogramu, limitu i odrzuceń.
const isDue = (c: OccasionalTheme, now: Date): boolean => {
  if (getDismissed().includes(c.id)) return false;
  if ((getImpressions()[c.id] ?? 0) >= c.schedule.maxImpressions) return false;

  if (c.schedule.anchor === "absolute") {
    const today = now.toISOString().slice(0, 10);
    if (c.schedule.from && today < c.schedule.from) return false;
    if (c.schedule.to && today > c.schedule.to) return false;
    return true;
  }
  // accountActivation: dzień od aktywacji musi być w kadencji.
  const day = daysBetween(getActivationDate(), now);
  return (c.schedule.cadenceDays ?? []).includes(day);
};

// Pełna lista kampanii — do prezentacji w menu motywów (grupa „okazjonalne"),
// niezależnie od harmonogramu (ręczny podgląd na żądanie).
export const OCCASIONAL_THEMES: OccasionalTheme[] = MOCK_CAMPAIGNS;

// Kampania po id (lub null) — do ręcznego wyboru motywu z menu.
export const getOccasionalById = (id: string): OccasionalTheme | null =>
  MOCK_CAMPAIGNS.find((c) => c.id === id) ?? null;

// Najważniejsza należna kampania (lub null).
export const getDueOccasionalTheme = (now: Date = new Date()): OccasionalTheme | null => {
  const due = MOCK_CAMPAIGNS.filter((c) => isDue(c, now)).sort(
    (a, b) => b.priority - a.priority,
  );
  return due[0] ?? null;
};

// Zarejestrowanie wyświetlenia (BE: POST …/{id}/impression).
export const recordImpression = (id: string) => {
  const imp = getImpressions();
  imp[id] = (imp[id] ?? 0) + 1;
  writeJson(IMPRESSIONS_LS, imp);
};

// Trwałe odrzucenie kampanii (BE: POST …/{id}/dismiss).
export const dismissOccasional = (id: string) => {
  const list = getDismissed();
  if (!list.includes(id)) writeJson(DISMISSED_LS, [...list, id]);
};

// Reset stanu DEMO (tylko atrapa) — czyści datę aktywacji, licznik wyświetleń
// i odrzucenia, dzięki czemu pętla powitalny → webinar startuje od nowa.
// Po wdrożeniu BE ta funkcja znika razem z resztą atrapy.
export const resetOccasionalDemo = () => {
  try {
    localStorage.removeItem(ACTIVATION_LS);
    localStorage.removeItem(IMPRESSIONS_LS);
    localStorage.removeItem(DISMISSED_LS);
  } catch {
    /* brak dostępu do localStorage — ignorujemy */
  }
};
